import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import ICONS from "../../../assets/Icons";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import TEXT from "../../../utils/globalText";
import useCategories from "../../../utils/useCategories";
import SearchField from "../CommonSearchComponents/SearchField";
import SVGComponent from "../SVGComponent";
import TickerAnimation from "../TickerAnimation";
/**
 * Props for the CategorySelector component.
 *
 * @typedef {Object} CategorySelectorProps
 * @property {string} [beneAccountId=""]
 * Unique beneficiary account ID used for assign/unassign category navigation.
 *
 * @property {string[]} [category=[]]
 * List of category IDs currently assigned to the beneficiary account.
 *
 * @property {Object|null} [selectedCategory=null]
 * Currently selected category object (matched by `id`).
 *
 * @property {Array} [unassignCategories=[]]
 * List of active category objects (filtered here against `category` ids).
 *
 * @property {Array} [unassignlength=[]]
 * List/count reference used to determine whether unassigned categories
 * and the assign/unassign CTA should be displayed.
 *
 * @property {boolean} [disableCategories=false]
 * Disables interaction with unassigned categories when enabled.
 *
 * @property {Function} [handleCategorySelect=() => {}]
 * Called with the category ID when an assigned category is selected.
 *
 * @property {Function} [handleUnassignNavigate=() => {}]
 * Handles navigation to the assign/unassign category screen.
 *
 * @property {boolean} [previewMode=false]
 * Displays the selected category in preview mode instead of the category list.
 *
 * @property {"list"|"selected"} [variant="list"]
 * Controls the category display mode.
 *
 * @property {boolean} [enableSearch=false]
 * Enables category search.
 *
 * @property {boolean} [showUnassignCTA=true]
 * Shows or hides the assign/unassign category CTA.
 *
 * @property {string} [header]
 * @property {string} [headerNote]
 * @property {string} [subHeader]
 * @property {boolean} [hideHeader=false]
 * @property {boolean} [hideSubHeader=false]
 * @property {boolean} [shouldHideCheckbox=false]
 */

/**
 * CategorySelector component.
 *
 * @param {CategorySelectorProps} props
 * @returns {JSX.Element}
 */

const CategorySelector = ({
  beneAccountId = "",
  category = [],
  selectedCategory = null,
  unassignCategories = [],
  unassignlength = [],
  disableCategories = false,
  handleCategorySelect = () => {},
  handleUnassignNavigate = () => {},
  previewMode = false,
  variant = "list",
  enableSearch = false,
  showUnassignCTA = true,
  header,
  headerNote,
  subHeader,
  hideHeader = false,
  hideSubHeader = false,
  shouldHideCheckbox = false,
}) => {
  const categoryMapper = useCategories();
  const searchRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const categoriesFromRedux =
    useSelector((state) => state.sharedResources?.categoriesData?.categories) ||
    [];

  const categoryMetaMap = categoriesFromRedux.reduce((acc, cat) => {
    acc[cat.id] = cat;
    return acc;
  }, {});

  const assignedCategories = (category || [])
    .map((id) => categoryMetaMap[id])
    .filter(Boolean);

  const search = searchText.trim().toLowerCase();

  const filteredAssigned = assignedCategories.filter(
    (c) => !enableSearch || c.name.toLowerCase().includes(search),
  );

  const filteredUnassigned = unassignCategories.filter(
    (c) =>
      !category.includes(c.id) &&
      (!enableSearch || c.name.toLowerCase().includes(search)),
  );

  const selectedId = selectedCategory?.id;

  const resolvedCategory =
    (selectedId && categoryMetaMap[selectedId]) ||
    (selectedId ? selectedCategory : null) ||
    assignedCategories[0] ||
    {};

  const categoryName = resolvedCategory?.name;
  const showSelectedCard = variant === "selected" && categoryName;

  if (showSelectedCard) {
    const meta = resolvedCategory;
    const description = meta.description || "";
    const categoryIcon = categoryMapper(meta?.id);

    return (
      <div className="w-width-full bg-primary-card rounded-border-radius-md p-padding-md">
        <p className="text-text-primary font-semibold text-base mb-margin-md">
          {header?.trim() ? header : TEXT.TRANSACTION_CATEGORY}
        </p>
        <p className="text-text-primary text-sm font-semibold pb-padding-md">
          {TEXT.SELECTED_CATEGORY}
        </p>
        <div className="bg-primary-nav rounded-border-radius-md flex w-width-full">
          <div className="bg-text-primary p-padding-sm flex items-center justify-center rounded-l-border-radius-md">
            <div className="w-icon-lg h-icon-lg flex-shrink-0">
              <SVGComponent
                url={categoryIcon?.url}
                color={categoryIcon?.colour}
              />
            </div>
          </div>

          <div className="flex flex-col justify-center flex-1 pr-padding-md pl-padding-sm py-padding-sm">
            <p className="text-text-primary font-semibold text-sm">
              <TickerAnimation text={categoryName} />
            </p>
            {description && (
              <p className="text-text-secondary text-xs mt-margin-xs">
                <TickerAnimation text={description} />
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (previewMode && selectedCategory?.name) {
    const meta = resolvedCategory;
    const description = meta.description || "";
    const categoryIcon = categoryMapper(meta?.id);

    return (
      <div className="w-width-full bg-primary-card rounded-border-radius-md p-padding-md text-text-primary">
        {!hideHeader && (
          <p className="text-text-primary text-base font-semibold pb-padding-md">
            {header?.trim() ? header : TEXT.TRANSACTION_CATEGORY}
          </p>
        )}

        <div className="bg-primary-nav rounded-border-radius-md flex relative z-z-index-base w-width-full">
          <div className="bg-text-primary p-padding-sm flex items-center justify-center rounded-l-border-radius-md">
            <div className="w-icon-lg h-icon-lg flex-shrink-0">
              <SVGComponent
                url={categoryIcon?.url}
                color={categoryIcon?.colour}
              />
            </div>
          </div>

          <div className="Qmd:w-width_xxl w-width-md relative z-z-index-base flex flex-col justify-center flex-1 pr-padding-md pl-padding-sm py-padding-sm rounded-r-border-radius-md transition-colors duration-animation-fast-s">
            <p className="w-width-full text-text-primary font-semibold text-sm">
              <TickerAnimation text={selectedCategory?.name} />
            </p>

            {description && (
              <p className="w-width-full text-text-primary text-xs mt-margin-xs">
                <TickerAnimation text={description} />
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-width-full">
      {category.length > GlobalConfiguration?.lengthCheckZero && (
        <div className="bg-primary-nav flex flex-col relative rounded-border-radius-md">
          <div className="bg-primary-card rounded-t-border-radius-md px-padding-md pt-padding-md">
            <p className="text-text-primary font-semibold text-base">
              {header?.trim() ? header : TEXT.TRANSACTION_CATEGORY}
            </p>

            {headerNote?.trim() && (
              <p className="text-text-secondary text-font-xs mt-margin-xs">
                {headerNote}
              </p>
            )}
            {enableSearch && (
              <div className="mt-margin-md">
                <SearchField
                  searchText={searchText}
                  searchRef={searchRef}
                  placeholder="Search category"
                  onInput={(value) => setSearchText(value)}
                  isDisabled={false}
                  searchBgColor="var(--primary-bg)"
                />
              </div>
            )}
          </div>

          <div className="bg-primary-card py-padding-md rounded-b-border-radius-md w-width-full">
            <div className="w-width-full overflow-y-auto max-h-max-height-succes-page px-padding-md pb-padding-md custom-scroll-container">
              {!hideSubHeader && (
                <p className="text-text-primary text-sm font-semibold pb-padding-md">
                  {subHeader?.trim() ? subHeader : TEXT?.ASSIGNED_CATEGORIES}
                </p>
              )}

              {assignedCategories.length ===
              GlobalConfiguration?.lengthCheckZero ? (
                <div className="text-font-xs text-text-secondary pb-padding-md">
                  {TEXT.NO_ASSIGNED_CATEGORIES_NOTE}
                </div>
              ) : (
                <div className="flex flex-col gap-gap-md cursor-pointer w-width-full">
                  {filteredAssigned.map((cat) => {
                    const isSelected = cat.id === selectedId;
                    const description = cat.description || "";
                    const categoryIcon = categoryMapper(cat.id);

                    return (
                      <div
                        key={cat.id}
                        className="rounded-border-radius-md cursor-pointer flex items-center relative z-z-index-base w-width-full"
                        onClick={() => handleCategorySelect(cat.id)}
                        style={{ background: "var(--text-primary)" }}
                      >
                        <div className="bg-text-primary p-padding-sm flex items-center justify-center rounded-l-border-radius-md">
                          <div className="w-icon-lg h-icon-lg flex-shrink-0">
                            <SVGComponent
                              url={categoryIcon?.url}
                              color={categoryIcon?.colour}
                            />
                          </div>
                        </div>

                        <div
                          className={`flex relative group z-z-index-base rounded-r-border-radius-md flex-1 items-center overflow-hidden ${
                            isSelected
                              ? "bg-primary-nav-act"
                              : "bg-primary-nav global-hover-white"
                          }`}
                        >
                          <div
                            className="absolute bg-positive-bg group-hover:bg-positive-bg-hover z-z-index-header"
                            style={{
                              transform: !isSelected ? "scale(0)" : "scale(1)",
                              transformOrigin: "right",
                              transition: !isSelected
                                ? "transform var(--animation-fast-xs) ease-in"
                                : "transform var(--animation-fast-s) ease-in",
                              height: "var(--height-full)",
                              width: "calc(var(--width-full) - var(--icon-lg))",
                            }}
                          ></div>
                          <div
                            className="absolute bg-positive-bg group-hover:bg-positive-bg-hover z-z-index-header"
                            style={{
                              transform: !isSelected ? "scale(0)" : "scale(1)",
                              transformOrigin: "left",
                              transition: !isSelected
                                ? "transform var(--animation-fast-xs) ease-in"
                                : "transform var(--animation-fast-s) ease-in",
                              left: "calc(var(--left-full) - var(--icon-lg))",
                              height: "var(--height-full)",
                              width: "var(--icon-lg)",
                              borderRadius:
                                "var(--border-base) var(--border-radius-md) var(--border-radius-md) var(--border-base)",
                            }}
                          ></div>
                          <div className="Qmd:w-width-xl w-width-md relative z-z-index-header flex flex-col gap-gap-xs items-center justify-between flex-1 pr-padding-md pl-padding-sm py-padding-sm transition-colors duration-animation-fast-s">
                            <p className="w-width-full text-text-primary font-semibold text-sm break-all pr-padding-xs">
                              <TickerAnimation text={cat.name} />
                            </p>

                            {description && (
                              <p className="w-width-full text-text-primary text-xs pr-padding-xs break-all">
                                <TickerAnimation text={description} />
                              </p>
                            )}
                          </div>

                          {!shouldHideCheckbox && (
                            <div className="pr-padding-md py-padding-md relative z-z-index-header">
                              <img
                                className="w-icon-md h-icon-md"
                                src={
                                  isSelected
                                    ? ICONS.RADIO_BUTTON_SELECTED
                                    : ICONS.RADIO_BUTTON_NOT_SELECTED
                                }
                                alt="radio"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCategorySelect(cat.id);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {enableSearch &&
                    searchText &&
                    filteredAssigned.length ===
                      GlobalConfiguration?.lengthCheckZero && (
                      <p className="text-text-secondary text-font-xs">
                        {TEXT?.NO_ASSIGNED_CATEGORIES_FOUND}
                      </p>
                    )}
                </div>
              )}

              {unassignlength.length > GlobalConfiguration?.lengthCheckZero &&
                unassignCategories.length >
                  GlobalConfiguration?.lengthCheckZero &&
                !category.includes("all") && (
                  <>
                    <p className="text-text-primary text-sm font-semibold py-padding-md">
                      {subHeader?.trim()
                        ? subHeader
                        : TEXT?.UNASSIGNED_CATEGORIES}
                    </p>

                    <div className="flex flex-col gap-gap-md text-text-secondary w-width-full">
                      {filteredUnassigned.map((uc) => {
                        const description = uc.description || "";
                        const categoryIcon = categoryMapper(uc.id);

                        return (
                          <div
                            key={uc.id}
                            className={`bg-primary-nav rounded-border-radius-md cursor-pointer flex relative z-z-index-base w-width-full ${
                              disableCategories
                                ? "opacity-light-opacity pointer-events-none"
                                : "opacity-base-opacity"
                            }`}
                          >
                            <div className="bg-text-primary p-padding-sm flex items-center justify-center rounded-l-border-radius-md">
                              <div className="w-icon-lg h-icon-lg flex-shrink-0">
                                <SVGComponent
                                  url={categoryIcon?.url}
                                  color={categoryIcon?.colour}
                                />
                              </div>
                            </div>

                            <div className="Qmd:w-width_xxl w-width-md flex flex-col justify-center flex-1 pr-padding-md pl-padding-sm py-padding-sm rounded-r-border-radius-md bg-primary-nav">
                              <p className="text-text-primary font-semibold text-font-sm min-w-min-width-base">
                                <TickerAnimation text={uc.name} />
                              </p>

                              {description && (
                                <p className="text-text-secondary text-xs pr-padding-xs break-all mt-margin-xs">
                                  <TickerAnimation text={description} />
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {enableSearch &&
                      searchText &&
                      filteredUnassigned.length ===
                        GlobalConfiguration?.lengthCheckZero && (
                        <p className="text-text-secondary text-xs">
                          {TEXT?.NO_UNASSIGNED_CATEGORIES_FOUND}
                        </p>
                      )}
                  </>
                )}
            </div>
          </div>

          {showUnassignCTA &&
            category.length > GlobalConfiguration?.lengthCheckZero &&
            unassignlength.length > GlobalConfiguration?.lengthCheckZero && (
              <div className="flex flex-col gap-gap-md">
                <div className="rounded-border-radius-md p-padding-sm">
                  <div
                    className="flex items-center cursor-pointer global-hover-white rounded-border-radius-md p-padding-sm relative z-z-index-base"
                    onClick={() => handleUnassignNavigate(beneAccountId)}
                  >
                    <span className="text-text-primary text-font-sm">
                      {TEXT.TO_ASSIGN_UNASSIGN_CATEGORY}
                    </span>

                    <div className="ml-auto">
                      <SVGComponent
                        url={ICONS?.ARROW_RIGHT_WHITE}
                        color="var(--text-primary)"
                        height="var(--icon-sm)"
                        width="var(--icon-sm)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default CategorySelector;
